import { AxiosError } from 'axios'
import * as React from 'react'
import * as Sentry from '@sentry/react'
import { Localized } from '@fluent/react'
import { isAxiosError } from '../../guards'
import Spinner from '../../components/Spinner'
import Error404 from '../../screens/app/Error404'
import PersistanceError from '../../screens/app/Draft/PersistanceError'

import './index.css'

/**
 * Function responsible for loading content.
 *
 * @param props props passed to {@link Load}.
 *
 * @return object to be merged into `props` before they are passed to the
 * component proper.
 */
type Loader<P, T> = (props: P) => Promise<T>;

/**
 * Component responsible for displaying error messages.
 */
type Handler = React.ComponentType<{ error: Error }>;

/**
 * A higher order component providing error-handling and displaying logic for
 * fetching data.
 *
 * @param loader   function responsible for loading props
 * @param propsToCompare   array of props to compare inside componentDidUpdate
 * @param l10nId   Id of translated loading message
 * @param Handler  component responsible for displaying errors
 * @param Progress component responsible for displaying progress indicator
 */
const Load = <Args extends {}, Value extends {}> (
  loader: Loader<Args, Value>,
  propsToCompare?: string[],
  l10nId?: string,
  Handler: Handler = DefaultHandler,
  Progress: React.ComponentType<{l10nId?: string}> = Spinner,
) => <Props extends {}> (
    Component: React.ComponentType<Props & Value>,
  ) => class Load extends React.Component<Args & Props> {
      static displayName = `Load(${Component.displayName || Component.name})`

      state: {
    value?: Value
    error?: Error
  } = {}

      private _isMounted = false

      async fetchData() {
        try {
          const value = await loader(this.props)
          this._isMounted && this.setState({ value })
        } catch (error) {
          this._isMounted && this.setState({ error })
          if (error?.response?.status !== 404) {
            Sentry.captureException(error)
          }
        }
      }

      componentDidUpdate(prevProps: { [key: string]: any }) {
        const update = (propsToCompare || []).some(name => {
          if (prevProps[name] !== this.props[name]) {
            return true
          }
          return false
        })

        if (update) {
          this.fetchData()
        }
      }

      componentDidMount() {
        this._isMounted = true
        this.fetchData()
      }

      componentWillUnmount() {
        this._isMounted = false
      }

      render() {
        const { value, error } = this.state

        if (error) return <Handler error={error} />

        if (value) return <Component {...this.props} {...value} />

        return <Progress l10nId={l10nId} />
      }
    }

export default Load

function DefaultHandler({ error }: { error: Error | AxiosError }) {
  if (isAxiosError(error) && error.response?.status === 404) {
    return <Error404 />
  }

  return (
    <div className="load-error">
      <Localized id="load-error-message" elems={{ p: <p/> }}>
        <div className="load-error__message" />
      </Localized>
      <div className="load-error__content">
        {
          error instanceof PersistanceError ?
            <Localized id="load-error-persistance">
              {error.toString()}
            </Localized>
            : error.toString()
        }
      </div>
    </div>
  )
}
